/**
 * LoadingButton — a submit-style button that swaps its label for a
 * spinner while in flight, WITHOUT shifting layout.
 *
 * Why the layout invariance matters: the previous pattern was
 *
 *     <button>{loading ? 'Triggering…' : 'Trigger'}</button>
 *
 * which reflowed every neighbour every time the operation kicked off,
 * because "Triggering…" is wider than "Trigger" (and every parallel
 * action button had a different reflow). The control jumping under the
 * cursor on click is a small papercut individually, but it shows up
 * everywhere in the app (deploy, trigger, save, add env var, …).
 *
 * Mechanism:
 *   - Render the resting label inside a `<span>` that stays in the DOM
 *     on every state, so the button measures the same width regardless
 *     of `loading`.
 *   - When `loading` is true, that span gets `visibility: hidden` (still
 *     occupies space) and an `<IonSpinner>` is absolutely-positioned at
 *     the centre of the button.
 *   - Disable + clear pointer-events while loading so a double-click
 *     can't re-trigger the same submit.
 *
 * Visual: a transparent passthrough — caller picks the look with a
 * `className`. We don't ship colours / paddings from here; the host
 * page's existing button styles (trigger-modal-btn-white, settings-
 * actions, etc.) keep working.
 *
 * Why IonSpinner: it's already in the bundle (Ionic ships it as part
 * of the core), matches the dot-circular spinner used elsewhere
 * (TriggerModal probe, DataTable loading), and respects prefers-
 * reduced-motion. Using a pure-CSS spinner here would have to
 * duplicate those affordances.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { IonSpinner } from '@ionic/react';
import './LoadingButton.css';

const LoadingButton = React.forwardRef(function LoadingButton(
  {
    loading = false,
    disabled = false,
    children,
    spinnerColor,
    type = 'button',
    className = '',
    onClick,
    ...rest
  },
  ref,
) {
  // The combined disabled gate keeps "loading" semantically separate
  // from "explicitly disabled" but applies the same effect, so a
  // disabled-loading button still ignores clicks if for some reason
  // the parent forgot one of the props.
  const isInert = loading || disabled;

  return (
    <button
      ref={ref}
      // `type` comes from props (defaults to "button"), validated by
      // PropTypes below. The eslint rule wants a literal — we expose
      // it as a prop so callers can use type="submit" inside <form>s.
      // eslint-disable-next-line react/button-has-type
      type={type}
      disabled={isInert}
      onClick={onClick}
      aria-busy={loading || undefined}
      className={`loading-btn ${loading ? 'loading-btn--loading' : ''} ${className}`.trim()}
      {...rest}
    >
      <span className="loading-btn__label">{children}</span>
      {loading && (
        <span className="loading-btn__spinner" aria-hidden="true">
          <IonSpinner
            name="crescent"
            style={spinnerColor ? { color: spinnerColor } : undefined}
          />
        </span>
      )}
    </button>
  );
});

LoadingButton.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  spinnerColor: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export default LoadingButton;
